import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Loader2,
  Play,
  Download,
  CheckCircle2,
  ClipboardList,
  CalendarRange,
  CalendarClock,
} from 'lucide-react';
import { ActivePlansTimeline } from '@/components/analytics/active-plans-timeline';
import { ExpirationCalendar } from '@/components/analytics/expiration-calendar';
import {
  getBookingsPastPlan,
  getUsersPlanAnomalies,
  type BookingsPastPlanReport,
  type BookingRefusedRow,
  type BookingRefusedReason,
  type UsersPlanAnomaliesReport,
  type UserAccessExpiredRow,
  type UserAccessExpiredReason,
  type UserMultiPlanRow,
} from '@/api/reports';
import {
  CronStatsCard,
  CronRunsCard,
  UserStatusChangesCard,
} from '@/app/(dashboard)/cron/page';
import { PageHeader } from '@/components/shared/page-header';

interface CheckRunState<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
}

const REASON_LABEL: Record<BookingRefusedReason, string> = {
  gymPayment_date_passed: 'gymPayment expiresAt passed',
  gymPayment_marked_expired: 'gymPayment isExpired flag',
  payment_end_date_passed: 'payment endDate passed (no gymPayment)',
  payment_expired_flag: 'payment expired flag (no gymPayment)',
  no_payment_link: 'no paymentId on booking',
};

const REASON_VARIANT: Record<
  BookingRefusedReason,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  gymPayment_date_passed: 'destructive',
  gymPayment_marked_expired: 'destructive',
  payment_end_date_passed: 'destructive',
  payment_expired_flag: 'secondary',
  no_payment_link: 'secondary',
};

function rowsToCsv(rows: BookingRefusedRow[]): string {
  const headers = [
    'bookingId',
    'date',
    'reason',
    'fullName',
    'userId',
    'classId',
    'paymentId',
    'gpFound',
    'gpExpiresAt',
    'gpIsExpired',
    'gpRemainingEntries',
    'planEndDate',
    'planStartDate',
    'paymentExpired',
    'paymentExpiredReason',
    'createdAt',
  ];
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape((r as any)[h])).join(','));
  }
  return lines.join('\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function BookingsRefusedCard() {
  const [state, setState] = useState<CheckRunState<BookingsPastPlanReport>>({
    loading: false,
    data: null,
    error: null,
  });

  const run = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const data = await getBookingsPastPlan();
      setState({ loading: false, data, error: null });
    } catch (err: any) {
      setState({
        loading: false,
        data: null,
        error: err?.message || 'Failed to run check',
      });
    }
  };

  const { loading, data, error } = state;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active bookings the system would refuse now</CardTitle>
        <CardDescription>
          For every active class booking, this check asks: if the user tried to book
          this today, would the booking flow accept it? It joins through
          user.gymPayments[] (the runtime expiry the booking flow actually
          validates against) and falls back to the linked payment&apos;s endDate.
          Future rows are actionable; past rows are historical.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={run} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run check
          </Button>
          {data && (
            <>
              <Badge variant="destructive">Future: {data.counts.future}</Badge>
              <span className="text-xs text-muted-foreground">as of {data.today}</span>
            </>
          )}
        </div>

        {data && (() => {
          const futureByReason = data.future.reduce(
            (acc, r) => {
              acc[r.reason] = (acc[r.reason] || 0) + 1;
              return acc;
            },
            {} as Record<BookingRefusedReason, number>,
          );
          const reasons = (
            Object.keys(futureByReason) as BookingRefusedReason[]
          ).filter((k) => futureByReason[k] > 0);
          if (reasons.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-2">
              {reasons.map((k) => (
                <Badge key={k} variant={REASON_VARIANT[k]}>
                  {REASON_LABEL[k]}: {futureByReason[k]}
                </Badge>
              ))}
            </div>
          );
        })()}

        {error && <div className="text-sm text-destructive">{error}</div>}

        {data && data.future.length > 0 && (
          <ReportSection
            title="Future bookings (actionable)"
            filename={`bookings-refused-future-${data.today}.csv`}
            rows={data.future}
          />
        )}

        {data && data.counts.total === 0 && (
          <div className="text-sm text-muted-foreground">
            No bookings would be refused. Nothing to do.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportSection({
  title,
  filename,
  rows,
}: {
  title: string;
  filename: string;
  rows: BookingRefusedRow[];
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">
          {title} ({rows.length})
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadCsv(filename, rowsToCsv(rows))}
        >
          <Download className="h-4 w-4 mr-2" />
          CSV
        </Button>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>gp expiresAt</TableHead>
              <TableHead>plan endDate</TableHead>
              <TableHead>Booking ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 50).map((r) => (
              <TableRow key={r.bookingId}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell>{r.fullName}</TableCell>
                <TableCell>
                  <Badge variant={REASON_VARIANT[r.reason]}>
                    {REASON_LABEL[r.reason]}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {r.gpFound ? (
                    <>
                      {r.gpExpiresAt || '—'}
                      {r.gpIsExpired && (
                        <span className="ml-1 text-destructive">(expired)</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">no match</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {r.planEndDate || '—'}
                  {r.paymentExpired && (
                    <span className="ml-1 text-destructive">(expired)</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{r.bookingId}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length > 50 && (
        <div className="text-xs text-muted-foreground">
          Showing first 50 of {rows.length}. Download CSV for the full list.
        </div>
      )}
    </div>
  );
}

const USER_REASON_LABEL: Record<UserAccessExpiredReason, string> = {
  only_legacy_expired: 'legacy activePayment expired (no gymPayments)',
  all_gym_payments_expired: 'all gymPayments expired (no legacy activePayment)',
  legacy_and_gym_payments_expired: 'legacy + all gymPayments expired',
};

const USER_REASON_VARIANT: Record<
  UserAccessExpiredReason,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  only_legacy_expired: 'secondary',
  all_gym_payments_expired: 'destructive',
  legacy_and_gym_payments_expired: 'destructive',
};

function noValidPlanCsv(rows: UserAccessExpiredRow[]): string {
  const headers = [
    'userId',
    'name',
    'surname',
    'email',
    'phone',
    'reason',
    'hasActivePayment',
    'legacyEndDate',
    'legacyPaymentExpired',
    'legacyPaymentExpiredReason',
    'legacyIsFixed',
    'legacyRemainingEntries',
    'totalGymPayments',
    'validGymPayments',
    'lastGpExpiresAt',
    'gpFixedCount',
    'gpFixedEntriesRemaining',
    'gpNonFixedCount',
    'createdAt',
    'updatedAt',
  ];
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape((r as any)[h])).join(','));
  }
  return lines.join('\n');
}

function multiPlanCsv(rows: UserMultiPlanRow[]): string {
  const headers = [
    'userId',
    'name',
    'surname',
    'email',
    'phone',
    'legacyEndDate',
    'legacyIsFixed',
    'legacyRemainingEntries',
    'totalGymPayments',
    'validGymPayments',
    'latestGpExpiresAt',
    'gpFixedCount',
    'gpFixedEntriesRemaining',
    'gpNonFixedCount',
    'createdAt',
  ];
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape((r as any)[h])).join(','));
  }
  return lines.join('\n');
}

function UsersPlanAnomaliesCard() {
  const [state, setState] = useState<CheckRunState<UsersPlanAnomaliesReport>>({
    loading: false,
    data: null,
    error: null,
  });

  const run = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const data = await getUsersPlanAnomalies();
      setState({ loading: false, data, error: null });
    } catch (err: any) {
      setState({
        loading: false,
        data: null,
        error: err?.message || 'Failed to run check',
      });
    }
  };

  const { loading, data, error } = state;

  return (
    <Card>
      <CardHeader>
        <CardTitle>User plan anomalies</CardTitle>
        <CardDescription>
          Active users (<code className="text-xs">userType: user</code>) whose plan
          state is unusual:
          <span className="block mt-1">
            <strong>No valid plan</strong> — has plan record(s), all expired. Should
            have lost access but is still marked active.
          </span>
          <span className="block mt-1">
            <strong>Multiple plans</strong> — has a valid legacy{' '}
            <code className="text-xs">activePayment</code> AND at least one valid
            entry in <code className="text-xs">gymPayments[]</code>. Usually means
            the legacy field wasn&apos;t cleared during migration.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={run} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run check
          </Button>
          {data && (
            <>
              <Badge variant="destructive">
                No valid plan: {data.noValidPlan.total}
              </Badge>
              <Badge variant="secondary">
                Multiple plans: {data.multiplePlans.total}
              </Badge>
              <span className="text-xs text-muted-foreground">
                as of {data.today}
              </span>
            </>
          )}
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}

        {data && (
          <>
            {/* No valid plan section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Users with no valid plan ({data.noValidPlan.total})
                </h3>
                {data.noValidPlan.users.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      downloadCsv(
                        `users-no-valid-plan-${data.today}.csv`,
                        noValidPlanCsv(data.noValidPlan.users),
                      )
                    }
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                )}
              </div>

              {data.noValidPlan.total > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(
                    Object.keys(
                      data.noValidPlan.byReason,
                    ) as UserAccessExpiredReason[]
                  )
                    .filter((k) => data.noValidPlan.byReason[k] > 0)
                    .map((k) => (
                      <Badge key={k} variant={USER_REASON_VARIANT[k]}>
                        {USER_REASON_LABEL[k]}: {data.noValidPlan.byReason[k]}
                      </Badge>
                    ))}
                </div>
              )}

              {data.noValidPlan.users.length > 0 ? (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Legacy endDate</TableHead>
                        <TableHead>gymPayments (valid / total)</TableHead>
                        <TableHead>Last gp expiresAt</TableHead>
                        <TableHead>Fixed gp</TableHead>
                        <TableHead>Non-fixed gp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.noValidPlan.users.slice(0, 50).map((u) => (
                        <TableRow key={u.userId}>
                          <TableCell>
                            {u.surname} {u.name}
                          </TableCell>
                          <TableCell className="text-xs">
                            {u.email || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={USER_REASON_VARIANT[u.reason]}>
                              {USER_REASON_LABEL[u.reason]}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.hasActivePayment ? (
                              <>
                                {u.legacyEndDate || '—'}
                                {u.legacyPaymentExpired && (
                                  <span className="ml-1 text-destructive">
                                    (expired)
                                  </span>
                                )}
                                {u.legacyIsFixed && (
                                  <span className="ml-1 text-muted-foreground">
                                    (fixed
                                    {typeof u.legacyRemainingEntries === 'number'
                                      ? `, ${u.legacyRemainingEntries} left`
                                      : ''}
                                    )
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground">none</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.validGymPayments} / {u.totalGymPayments}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.lastGpExpiresAt || '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.gpFixedCount > 0 ? (
                              <>
                                {u.gpFixedCount} plan{u.gpFixedCount === 1 ? '' : 's'}
                                <span className="ml-1 text-muted-foreground">
                                  ({u.gpFixedEntriesRemaining} entries)
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.gpNonFixedCount > 0 ? (
                              <>
                                {u.gpNonFixedCount} plan
                                {u.gpNonFixedCount === 1 ? '' : 's'}
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  None — every active user with a plan record has a valid plan.
                </div>
              )}
              {data.noValidPlan.users.length > 50 && (
                <div className="text-xs text-muted-foreground">
                  Showing first 50 of {data.noValidPlan.users.length}. Download CSV
                  for the full list.
                </div>
              )}
            </div>

            {/* Multiple plans section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Users with multiple active plans ({data.multiplePlans.total})
                </h3>
                {data.multiplePlans.users.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      downloadCsv(
                        `users-multi-plan-${data.today}.csv`,
                        multiPlanCsv(data.multiplePlans.users),
                      )
                    }
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                )}
              </div>

              {data.multiplePlans.users.length > 0 ? (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Legacy endDate</TableHead>
                        <TableHead>gymPayments (valid / total)</TableHead>
                        <TableHead>Latest gp expiresAt</TableHead>
                        <TableHead>Fixed gp</TableHead>
                        <TableHead>Non-fixed gp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.multiplePlans.users.slice(0, 50).map((u) => (
                        <TableRow key={u.userId}>
                          <TableCell>
                            {u.surname} {u.name}
                          </TableCell>
                          <TableCell className="text-xs">
                            {u.email || '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.legacyEndDate || '—'}
                            {u.legacyIsFixed && (
                              <span className="ml-1 text-muted-foreground">
                                (fixed
                                {typeof u.legacyRemainingEntries === 'number'
                                  ? `, ${u.legacyRemainingEntries} left`
                                  : ''}
                                )
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.validGymPayments} / {u.totalGymPayments}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.latestGpExpiresAt || '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.gpFixedCount > 0 ? (
                              <>
                                {u.gpFixedCount} plan{u.gpFixedCount === 1 ? '' : 's'}
                                <span className="ml-1 text-muted-foreground">
                                  ({u.gpFixedEntriesRemaining} entries)
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {u.gpNonFixedCount > 0 ? (
                              <>
                                {u.gpNonFixedCount} plan
                                {u.gpNonFixedCount === 1 ? '' : 's'}
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  None — no active users have both a valid legacy plan and a valid
                  gymPayments entry.
                </div>
              )}
              {data.multiplePlans.users.length > 50 && (
                <div className="text-xs text-muted-foreground">
                  Showing first 50 of {data.multiplePlans.users.length}. Download CSV
                  for the full list.
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

type AnalyticsTab = 'analytics' | 'expirations' | 'verify-usage' | 'cron';

export default function ReportsPage() {
  const location = useLocation();
  // /cron route mounts the same page but defaults to the "Cron" tab.
  const initialTab: AnalyticsTab = location.pathname.startsWith('/cron')
    ? 'cron'
    : 'analytics';
  const [tab, setTab] = useState<AnalyticsTab>(initialTab);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Analytics"
        description="Active plan timeline, ad-hoc data integrity checks, and cron/usage telemetry for the current tenant."
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as AnalyticsTab)}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4" />
            Active plans timeline
          </TabsTrigger>
          <TabsTrigger value="expirations" className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Expiration calendar
          </TabsTrigger>
          <TabsTrigger value="verify-usage" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Verify usage
          </TabsTrigger>
          <TabsTrigger value="cron" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Cron
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-6 mt-4">
          <ActivePlansTimeline />
        </TabsContent>

        <TabsContent value="expirations" className="space-y-6 mt-4">
          <ExpirationCalendar />
        </TabsContent>

        <TabsContent value="verify-usage" className="space-y-6 mt-4">
          <BookingsRefusedCard />
          <UsersPlanAnomaliesCard />
        </TabsContent>

        <TabsContent value="cron" className="space-y-6 mt-4">
          <CronStatsCard />
          <CronRunsCard />
          <UserStatusChangesCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
